import { ReactElement, useMemo, useState, useEffect, useRef } from 'react';
import AmountInput from '@components/AmountInput';
import Button from '@components/Button';
import CronosWarpPoolRadioButtons from '@components/CronosWarpPoolRadioButtons';
import FormField from '@components/FormField';
import NetworkSelectionModule from '@components/NetworkSelectionModule';
import SelectAssetButton from '@components/SelectAssetButton';
import StackView from '@components/StackView';
import TransferTransactionSummary from '@components/TransferTransactionSummary';
import WalletAddress from '@components/WalletAddress';
import WalletAddressInput from '@components/WalletAddressInput';
import WalletAddressWithConnectSecondWalletButton from '@components/WalletAddressWithConnectSecondWalletButton';
import WarningBox from '@components/WarningBox';
import ExternalBridgesInfo from '@components/ExternalBridgesInfo';
import useDebouncedValue from '@hooks/useDebouncedValue';
import useBridgeTransactionFee, {
  UseBridgeTransactionFeeParams,
} from '@hooks/useBridgeTransactionFee';
import useGetERC20Approval from '@hooks/useGetERC20Approval';
import useGetErrorMessage from '@hooks/useGetErrorMessage';
import useGetReceiveAmount from '@hooks/useGetReceiveAmount';
import useNetworkEnv from '@hooks/useNetworkEnv';
import useShouldGetERC20Approval from '@hooks/useShouldGetERC20Approval';
import useTokenBalance, { TokenBalanceStatus } from '@hooks/useTokenBalance';
import useTransferAssetFlowContext from '@hooks/useTransferAssetFlowContext';
import useWallets from '@hooks/useWallets';
import { ValidationField } from '@hooks/useValidateTransferAssetFlow';
import { CronosEthereumTransferType, Network, WalletType } from '@types';
import { TransferAssetFlowStep } from '@providers/TransferAssetFlowProvider';
import useIsInvalidReceiveAmount from './useIsInvalidReceiveAmount';
import {
  getDisplaySymbol,
  getHasAddressInput,
  isAmountZeroEquivalent,
  isEthChain,
  IsNativeToken,
  isWalletSupportedByNetwork,
  minBalanceForSymbol,
} from '@lib/utils';
import ERC20Disclaimer from './ERC20Disclaimer';
import useShouldRenderSummary from './useShouldRenderSummary';

const getMaxAvailableBalance = (
  balance: number,
  network: Network,
  symbol?: string
): number => {
  if (!IsNativeToken({ network: network, symbol: symbol ?? '' })) {
    return balance;
  }
  const minBalance = minBalanceForSymbol(symbol);
  const offsetBal = balance - minBalance;
  return offsetBal > 0 ? offsetBal : 0;
};

function ProvideTransferDetailsView(): ReactElement {
  const {
    onAmountChange,
    onConfirmFeeInfo,
    onCronosEthereumTransferTypeChange,
    onFlipNetworks,
    onFromNetworkChange,
    onNextStep,
    onShowAssetSelection,
    onShowWalletConnection,
    onToAddressChange,
    onInitialToNetworkChange,
    onTokenBalanceChange,
    onToNetworkChange,
    onValidate,
    state,
  } = useTransferAssetFlowContext();
  const [isAmountInputDirty, setIsAmountInputDirty] = useState(false);
  const lastAmountRef = useRef('0');
  const getErrorMessage = useGetErrorMessage();
  const getReceiveAmount = useGetReceiveAmount();
  const { wallets } = useWallets();
  const [isTestnet] = useNetworkEnv();
  const { balance, status: tokenBalanceStatus } = useTokenBalance(
    wallets[0],
    state.tokenAsset!
  );
  const shouldRenderSummary = useShouldRenderSummary({
    amount: state.amount,
    connectedWalletsCount: wallets.length,
    tokenAsset: state.tokenAsset,
  });
  const isInvalidReceiveAmount = useIsInvalidReceiveAmount();
  const showCronosEthereumTransferMode =
    state.fromNetwork === Network.Cronos &&
    state.toNetwork === Network.Ethereum &&
    !!state.cronosEthereumTransferType;
  const { shouldGetERC20Approval, checkingERC20Allowance } =
    useShouldGetERC20Approval({
      wallet: wallets[0],
      amount: state.amount,
      tokenAsset: state.tokenAsset,
      fromNetwork: state.fromNetwork,
      toNetwork: state.toNetwork,
      toAddress: state.toAddress,
    });
  const { ERC20Approved, ERC20Approving, getERC20Approval } =
    useGetERC20Approval({
      wallet: wallets[0],
      tokenAsset: state.tokenAsset,
    });
  const hasAddressInput = getHasAddressInput(
    state.fromNetwork,
    state.toNetwork,
    isTestnet
  );
  const bridgeTransactionFeeParams =
    useMemo<UseBridgeTransactionFeeParams | null>(() => {
      if (!shouldRenderSummary || !state.tokenAsset) {
        return null;
      }

      return {
        amount: state.amount,
        cronosEthereumTransferType: state.cronosEthereumTransferType,
        fromNetwork: state.fromNetwork,
        toNetwork: state.toNetwork,
        tokenAsset: state.tokenAsset,
      };
    }, [
      shouldRenderSummary,
      state.amount,
      state.cronosEthereumTransferType,
      state.fromNetwork,
      state.toNetwork,
      state.tokenAsset,
    ]);
  const debouncedBridgeTransactionFeeParams = useDebouncedValue(
    bridgeTransactionFeeParams,
    500
  );
  const {
    feeInfo,
    hasError: hasFeeError,
    isLoading: isLoadingFee,
  } = useBridgeTransactionFee(debouncedBridgeTransactionFeeParams);
  const receiveAmount = getReceiveAmount(
    state.amount,
    feeInfo?.amount || '0',
    feeInfo?.tokenAsset?.decimals
  );
  const isAllWalletsActive = useMemo(
    () => wallets.reduce((a, { active }) => a && active, true),
    [wallets]
  );

  const handleToAutoChange = (toNetwork: Network) => {
    if (!state.hasSwitchedNetwork) {
      onInitialToNetworkChange(toNetwork);
    } else {
      onToNetworkChange(toNetwork);
    }
  };

  function getFieldErrorMessage(key: ValidationField): string | null {
    if (
      !state.validationErrors[key] ||
      state.validationErrors[key]!.length === 0
    ) {
      return null;
    }

    return getErrorMessage(state.validationErrors[key]![0]);
  }

  useEffect(() => {
    if (isAmountInputDirty && state.amount !== lastAmountRef.current) {
      lastAmountRef.current = state.amount;
      onValidate({
        fields: ['amount'],
        step: TransferAssetFlowStep.ProvideDetails,
      });
    }
  }, [isAmountInputDirty, state.amount, onValidate]);

  useEffect(() => {
    onTokenBalanceChange(balance);
  }, [onTokenBalanceChange, balance]);

  const getTokenBalanceDisplayText = () => {
    const [firstWallet] = wallets;
    if (
      tokenBalanceStatus === TokenBalanceStatus.PENDING &&
      state.tokenAsset &&
      firstWallet
    ) {
      return 'Fetching balance...';
    }

    return state.tokenBalance && `${state.tokenBalance} available`;
  };

  const displayWarningBox = () => {
    if (
      state.fromNetwork === Network.Ethereum ||
      state.toNetwork === Network.Ethereum
    ) {
      return (
        <WarningBox level="info">
          Ethereum transfer may take from a few minutes up to hours depending on
          the network congestion.
        </WarningBox>
      );
    }

    if (
      (state.fromNetwork === Network.CryptoOrg ||
        state.toNetwork === Network.CryptoOrg) &&
      wallets.length < 2
    ) {
      return (
        <WarningBox level="danger">
          Please do not use centralized exchange wallet addresses.
        </WarningBox>
      );
    }

    return null;
  };

  const displayCTAButton = () => {
    const hasErrors = Object.keys(state.validationErrors).length ? true : false;
    const disableTransferAssetButton =
      hasErrors ||
      isAmountZeroEquivalent(state.amount) ||
      isInvalidReceiveAmount(receiveAmount, feeInfo?.tokenAsset?.decimals) ||
      !isAllWalletsActive;

    if (wallets.length === 0) {
      return (
        <Button className="w-full" onClick={onShowWalletConnection}>
          Connect Wallet
        </Button>
      );
    } else {
      if (state.tokenAsset && shouldGetERC20Approval) {
        if (ERC20Approving) {
          return (
            <Button className="w-full" disabled={true}>
              Waiting for approval...
            </Button>
          );
        }
        if (!ERC20Approved) {
          return (
            <Button className="w-full" onClick={getERC20Approval}>
              Enable {state.tokenAsset.symbol} Contract
            </Button>
          );
        }
      }
      if (state.tokenAsset && checkingERC20Allowance) {
        return (
          <Button className="w-full" disabled={true}>
            Checking {state.tokenAsset.symbol} Contract Allowance
          </Button>
        );
      }
      if (state.toAddress === '0x0000000000000000000000000000000000000000') {
        return (
          <Button className="w-full" disabled={true}>
            Invalid Receiver Address
          </Button>
        );
      }
      return (
        <Button
          className="w-full"
          onClick={async () => {
            const { ok } = await onValidate({
              fields: null,
              step: TransferAssetFlowStep.ProvideDetails,
            });

            if (ok && feeInfo) {
              onConfirmFeeInfo(feeInfo);
              onNextStep();
            }
          }}
          disabled={disableTransferAssetButton || hasFeeError || isLoadingFee}
        >
          Transfer Assets
        </Button>
      );
    }
  };

  const displayDestinationAddress = () => {
    const [firstWallet, secondWallet] = wallets;

    if (
      firstWallet?.type === WalletType.deficonnect &&
      !secondWallet &&
      isWalletSupportedByNetwork(
        WalletType.deficonnect,
        state.toNetwork,
        isTestnet
      ) &&
      !(isEthChain(state.fromNetwork) && isEthChain(state.toNetwork))
    ) {
      return (
        <WalletAddressWithConnectSecondWalletButton
          walletAddress={state.toAddress}
          onConnectSecondWallet={onShowWalletConnection}
        />
      );
    } else if (hasAddressInput && secondWallet?.address) {
      return (
        <WalletAddress
          walletAddress={state.toAddress}
          walletType={wallets[1].type}
        />
      );
    } else if (hasAddressInput) {
      return (
        <FormField
          errorMessage={getFieldErrorMessage(ValidationField.toAddress)}
        >
          <WalletAddressInput
            onBlur={(): void => {
              onValidate({
                fields: ['toAddress', 'tokenAsset', 'amount'],
                step: TransferAssetFlowStep.ProvideDetails,
              });
            }}
            onChange={(e): void => onToAddressChange(e.target.value)}
            onConnectSecondWallet={onShowWalletConnection}
            value={state.toAddress}
          />
        </FormField>
      );
    } else if (firstWallet?.address && !hasAddressInput) {
      return (
        <WalletAddress
          walletAddress={state.toAddress}
          walletType={wallets[0].type}
        />
      );
    }

    return null;
  };

  return (
    <StackView direction="vertical" spacing={2}>
      <NetworkSelectionModule
        fromOnChange={(network: Network) => {
          onFromNetworkChange(network);
        }}
        fromValue={state.fromNetwork}
        onFlipNetworks={onFlipNetworks}
        toOnChange={onToNetworkChange}
        toOnAutoChange={handleToAutoChange}
        toValue={state.toNetwork}
      />
      <div className="flex flex-col">
        <div className="tablet:flex mb-2">
          <FormField
            className="mb-2 tablet:mb-0 tablet:mr-2"
            errorMessage={getFieldErrorMessage(ValidationField.tokenAsset)}
          >
            <SelectAssetButton
              className="w-full"
              iconClassName="h-[28px] w-[28px]"
              onClick={onShowAssetSelection}
              tokenAsset={state.tokenAsset}
            />
          </FormField>
          <FormField
            className="min-w-0"
            infoMessage={getTokenBalanceDisplayText()}
            infoMessageAlignment="right"
            errorMessage={getFieldErrorMessage(ValidationField.amount)}
            errorMessageAlignment="right"
          >
            <AmountInput
              disableMaxButton={!state.tokenAsset}
              onBlur={(e): void => {
                const clickedOnMaxButton =
                  // @ts-ignore
                  e.relatedTarget?.getAttribute('name') ===
                  'amount-input-max-button';

                if (!clickedOnMaxButton) {
                  onValidate({
                    fields: ['amount', 'tokenAsset'],
                    step: TransferAssetFlowStep.ProvideDetails,
                  });
                }
              }}
              onChange={(e): void => {
                setIsAmountInputDirty(true);
                onAmountChange(e.target.value);
              }}
              onMaxClick={(): void => {
                onAmountChange(
                  getMaxAvailableBalance(
                    parseFloat(state.tokenBalance),
                    state.fromNetwork,
                    state.tokenAsset?.symbol
                  ).toString(),
                  {
                    clearAmountValidationErrors: !isAmountZeroEquivalent(
                      state.tokenBalance
                    ),
                  }
                );
              }}
              value={state.amount}
            />
          </FormField>
        </div>
        {displayDestinationAddress()}
      </div>
      {displayWarningBox()}
      {showCronosEthereumTransferMode && (
        <CronosWarpPoolRadioButtons
          onChange={(v): void => onCronosEthereumTransferTypeChange(v)}
          value={state.cronosEthereumTransferType as CronosEthereumTransferType}
        />
      )}
      {!!shouldRenderSummary && (
        <TransferTransactionSummary
          address={state.toAddress}
          feeAmount={
            feeInfo?.isWaived
              ? 'Waived'
              : `${feeInfo?.amount} ${getDisplaySymbol(
                  feeInfo?.tokenAsset.symbol || ''
                )}`
          }
          isLoading={!feeInfo}
          receiveAmount={`${receiveAmount} ${getDisplaySymbol(
            state.tokenAsset?.symbol || ''
          )}`}
          symbol={state.tokenAsset?.symbol || ''}
          toNetwork={state.toNetwork}
        />
      )}
      {displayCTAButton()}
      {wallets.length > 0 && shouldGetERC20Approval && !ERC20Approved && (
        <ERC20Disclaimer />
      )}
      <ExternalBridgesInfo className="mt-3" />
    </StackView>
  );
}

export default ProvideTransferDetailsView;
